import React, { useCallback, useEffect, useState } from 'react';

import {
  AtivacaoDoRadar,
  buscarAtivacaoDosRadares,
  definirAtivacaoDoRadar,
} from '../../../api/radarApi';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import { notify } from '../../../shared/feedback/notify';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../pages/css/adminSistema.css';

/**
 * A aba **Ativação** do Radar (transversal/at-83, ADR 0052).
 *
 * ## Por que ela existe
 *
 * A varredura de fundo do Radar do Relacionamento rodava 6 vezes por dia útil com paralelismo 8
 * para alimentar um painel que ninguém alcança (embargo da contac/at-18), e em 14/09/2026 o Cloud
 * SQL bateu no teto de conexões e derrubou telas de usuário (`BUG-007`). O interruptor é o que
 * permite parar um radar inteiro sem deploy.
 *
 * ## 🔴 Esta aba é a ÚNICA superfície do Radar que o TI alcança
 *
 * Ligar e desligar é operação de sistema (ADR 0012 dá ao TI); **ver os achados** é conteúdo
 * financeiro do Contac, que o mesmo ADR mantém fora do alcance dele. Por isso esta aba não mostra
 * número nenhum — só nome do radar, estado e autoria — e a aba de achados não é sequer montada
 * para quem não é ADMIN. A garantia real é do backend: `/radar/admin/avaliacao` e
 * `/radar/admin/exploracoes` continuam `@Roles(ADMIN)`.
 */

const formatarData = (iso: string | null): string => {
  if (!iso) return '';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  return data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

const AtivacaoDosRadaresSection: React.FC = () => {
  const [radares, setRadares] = useState<AtivacaoDoRadar[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [aConfirmar, setAConfirmar] = useState<AtivacaoDoRadar | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setRadares(await buscarAtivacaoDosRadares());
      setErro(false);
    } catch (error) {
      /*
       * Estado seguro E sinal (at-60). Aqui a lista vazia mentiria de um jeito específico: ela
       * diria "não há radar nenhum neste sistema", e o ADMIN concluiria que não tem o que ligar.
       */
      setRadares([]);
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar o estado dos Radares. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const alternar = useCallback(
    async (radar: AtivacaoDoRadar, ativo: boolean) => {
      setSalvando(radar.radarId);
      try {
        const atualizado = await definirAtivacaoDoRadar(radar.radarId, ativo);
        setRadares((atuais) =>
          atuais.map((r) => (r.radarId === atualizado.radarId ? atualizado : r)),
        );
        notify.success(
          ativo
            ? `${radar.rotulo} foi ativado. As análises voltam a rodar nos horários programados.`
            : `${radar.rotulo} foi desativado. Nenhuma análise dele será executada.`,
        );
      } catch (error) {
        /*
         * 🔴 Nada de falso sucesso, e nada de mover o switch antes da confirmação do servidor: a
         * tela não faz atualização otimista de propósito. Se ela movesse o botão e a gravação
         * falhasse, o ADMIN sairia acreditando que desligou um radar que continua consumindo.
         */
        /*
         * Texto FIXO, e nem `actionError` nem `errorFrom` (regra da at-65):
         *
         * - `actionError` conjuga a partir de um conjunto fechado de verbos (`create`, `update`…),
         *   e nenhum é "ativar"/"desativar" — forçar um produziria "Radar atualizado" para um
         *   clique que NÃO atualizou nada (BUG-V51);
         * - `errorFrom` prioriza a mensagem do backend, e aqui a CONSEQUÊNCIA importa mais que o
         *   motivo HTTP: o que o ADMIN precisa saber é **em que estado o Radar ficou**, senão sai
         *   daqui achando que desligou algo que continua consumindo o banco.
         */
        notify.error(
          ativo
            ? `Não foi possível ativar ${radar.rotulo}. O Radar continua desativado.`
            : `Não foi possível desativar ${radar.rotulo}. O Radar continua em funcionamento.`,
        );
      } finally {
        setSalvando(null);
      }
    },
    [],
  );

  return (
    <div className="admin-card">
      <h3>Ativação dos Radares</h3>

      <p className="admin-hint">
        Liga e desliga cada Radar. <strong>Desligado, o Radar para por completo</strong>: as
        análises automáticas não rodam, as varreduras de fundo não consultam nada e as telas dele
        passam a exibir um aviso de que a Administração do Sistema o desativou. Vale imediatamente,
        sem precisar de nova publicação do sistema.
      </p>

      <p className="admin-hint">
        Desligar <strong>não apaga nada</strong>: o histórico de achados e de votos continua
        guardado, e religar não reprocessa o que ficou para trás — o Radar volta a analisar a partir
        do momento em que for ligado.
      </p>

      {erro && (
        <p className="erro-de-tela">
          Não foi possível verificar o estado dos Radares. Esta lista pode estar incompleta — não
          conclua que não há Radares configurados.
        </p>
      )}

      {carregando && <p className="admin-hint">Carregando…</p>}

      {!carregando && !erro && radares.length === 0 && (
        <p className="admin-hint">Nenhum Radar está registrado neste sistema.</p>
      )}

      {radares.map((radar) => (
        <div key={radar.radarId} className="admin-card" style={{ marginTop: 12 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <strong>{radar.rotulo}</strong>{' '}
              <span
                className={`cilia-badge ${radar.ativo ? 'badge-verde' : 'badge-cinza'}`}
                style={{ marginLeft: 8 }}
              >
                {radar.ativo ? 'Ativo' : 'Desativado'}
              </span>
              <p className="admin-hint" style={{ margin: '6px 0 0' }}>
                {radar.alteradoPor && radar.alteradoEm
                  ? `${radar.ativo ? 'Ativado' : 'Desativado'} por ${radar.alteradoPor.nome} em ${formatarData(radar.alteradoEm)}.`
                  : 'Nunca foi ligado neste sistema.'}
              </p>
            </div>

            <button
              type="button"
              className={radar.ativo ? 'filtro-btn-limpar' : 'filtro-btn-aplicar'}
              disabled={salvando === radar.radarId}
              onClick={() => {
                // Desligar descreve o efeito antes de executar (padrão at-18/at-46). Ligar não
                // precisa: o efeito é o sistema voltar ao que se espera dele.
                if (radar.ativo) setAConfirmar(radar);
                else void alternar(radar, true);
              }}
            >
              {salvando === radar.radarId
                ? 'Salvando…'
                : radar.ativo
                  ? 'Desativar'
                  : 'Ativar'}
            </button>
          </div>
        </div>
      ))}

      {aConfirmar && (
        <ConfirmModal
          isOpen
          title={`Desativar ${aConfirmar.rotulo}?`}
          message={
            `O Radar para de funcionar por completo: as análises automáticas não rodam, as ` +
            `varreduras de fundo deixam de consultar dados e as telas dele passam a exibir um ` +
            `aviso de desativado. O histórico já registrado é mantido, e você pode religar a ` +
            `qualquer momento.`
          }
          confirmLabel="Desativar"
          onConfirm={() => {
            const radar = aConfirmar;
            setAConfirmar(null);
            void alternar(radar, false);
          }}
          onCancel={() => setAConfirmar(null)}
        />
      )}
    </div>
  );
};

export default AtivacaoDosRadaresSection;
